import { useEffect, useState } from "react";
import { DollarSign, Plus, Trash2, Edit3, X, Search, Save, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fmtBRL, HEADER_BG } from "../lib/constants";
import ConfirmDialog from "../components/ConfirmDialog";

export default function PriceListView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ device: "", service: "", cost: "", price: "", notes: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/price-list");
      setItems(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao carregar tabela.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return (
      (it.device || "").toLowerCase().includes(s) ||
      (it.service || "").toLowerCase().includes(s) ||
      (it.notes || "").toLowerCase().includes(s)
    );
  });

  const resetForm = () => {
    setForm({ device: "", service: "", cost: "", price: "", notes: "" });
    setEditingId(null);
    setErr("");
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      device: it.device || "",
      service: it.service || "",
      cost: it.cost ?? "",
      price: it.price ?? "",
      notes: it.notes || "",
    });
    setErr("");
  };

  const submit = async () => {
    if (!form.device.trim() && !form.service.trim()) {
      return setErr("Informe pelo menos o aparelho ou o serviço.");
    }
    setSaving(true);
    setErr("");
    try {
      const payload = {
        device: form.device.trim(),
        service: form.service.trim(),
        cost: parseFloat(form.cost) || 0,
        price: parseFloat(form.price) || 0,
        notes: form.notes.trim(),
      };
      if (editingId) {
        const r = await api.patch(`/price-list/${editingId}`, payload);
        setItems((prev) => prev.map((it) => (it.id === r.data.id ? r.data : it)));
      } else {
        const r = await api.post("/price-list", payload);
        setItems((prev) => [...prev, r.data]);
      }
      resetForm();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao salvar item.");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/price-list/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-400">Carregando...</div>;

  return (
    <div className="p-6 space-y-4" data-testid="price-list-view">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-blue-600" /> Tabela de preços
        </h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Registre os preços de custo e venda dos serviços/peças para consulta rápida quando houver dúvida.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-slate-500">Aparelho</label>
            <input value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })}
              placeholder="Ex.: iPhone 14 Pro" data-testid="price-device"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-slate-500">Serviço / Peça</label>
            <input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
              placeholder="Ex.: Troca de tela" data-testid="price-service"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500">Custo (R$)</label>
            <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
              data-testid="price-cost"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm text-right focus:border-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500">Venda (R$)</label>
            <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              data-testid="price-price"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm text-right focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="md:col-span-4">
            <label className="text-[10px] uppercase font-semibold text-slate-500">Observações</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ex.: Original, incolor, com garantia..." data-testid="price-notes"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="flex items-end justify-end gap-2">
            {editingId && (
              <button onClick={resetForm} className="h-10 px-3 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-200" data-testid="price-cancel">
                <X className="w-3.5 h-3.5 inline mr-1" /> Cancelar
              </button>
            )}
            <button onClick={submit} disabled={saving} data-testid="price-save"
              className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {editingId ? "Salvar" : "Adicionar"}
            </button>
          </div>
        </div>
        {err && (<div className="mx-5 mb-4 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>)}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aparelho, serviço, observação..."
              data-testid="price-search"
              className="w-full h-9 rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <span className="text-xs text-slate-500 tabular-nums">{filtered.length} item(s)</span>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400 italic">
            {items.length === 0 ? "Nenhum preço cadastrado ainda. Comece adicionando um item acima." : "Nenhum resultado."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: HEADER_BG }}>
                  {["APARELHO", "SERVIÇO / PEÇA", "CUSTO", "VENDA", "MARGEM", "OBS.", ""].map((h) => (
                    <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const margin = (Number(it.price) || 0) - (Number(it.cost) || 0);
                  return (
                    <tr key={it.id} className="border-t border-slate-100 hover:bg-blue-50/30">
                      <td className="px-3 py-2 text-xs font-semibold text-slate-800">{it.device || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{it.service || "—"}</td>
                      <td className="px-3 py-2 text-xs text-rose-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(it.cost)}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-blue-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(it.price)}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-emerald-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(margin)}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[220px] truncate" title={it.notes}>
                        {it.notes || <span className="italic text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => startEdit(it)} data-testid={`price-edit-${it.id}`} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setConfirmDel(it)} data-testid={`price-delete-${it.id}`} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        title="Excluir item"
        message={confirmDel ? `Excluir "${confirmDel.device} — ${confirmDel.service}"?` : ""}
        confirmLabel="Excluir"
        onCancel={() => setConfirmDel(null)}
        onConfirm={async () => { const id = confirmDel.id; setConfirmDel(null); await del(id); }}
      />
    </div>
  );
}
